import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';

// Enum değerleri
export const ResultStatus = {
    OK: 0,
    CREATED: 1,
    ERROR: 2,
    FORBIDDEN: 3,
    UNAUTHORIZED: 4,
    INVALID: 5,
    NOT_FOUND: 6,
    NO_CONTENT: 7,
    CONFLICT: 8,
    CRITICAL_ERROR: 9,
    UNAVAILABLE: 10
};

export const CompanySize = {
    MICRO: 0,
    SMALL: 1,
    MEDIUM: 2,
    LARGE: 3,
    ENTERPRISE: 4
};

// Mock storage for subsidiaries data
let mockSubsidiariesStorage = {};
// Mock storage for videos status
let mockVideosStorage = {};

export const tenantApi = createApi({
    reducerPath: 'tenantApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Tenant', 'TenantModules', 'TenantPermissions', 'Subsidiaries', 'Videos'],
    // Enhanced configuration for better synchronization
    refetchOnFocus: true,
    refetchOnReconnect: true,
    keepUnusedDataFor: 60, // 1 minute cache
    endpoints: (builder) => ({
        // **AUTH ENDPOINTS**
        // These endpoints don't require authentication (prepareHeaders will skip Authorization header for /Auth/ endpoints)
        loginGetCode: builder.mutation({
            query: (email) => ({
                url: `/Auth/GetCode/${encodeURIComponent(email)}`,
                method: 'GET',
            }),
            transformResponse: (response, meta, arg) => {
                if (!response?.isSuccess) {
                    throw new Error(response?.errors?.[0] || 'Failed to send verification code');
                }
                return {
                    success: true,
                    email: arg, // arg is the email parameter passed to query
                    value: response.value
                };
            },
        }),
        
        verifyAndGetToken: builder.mutation({
            query: ({ email, code }) => ({
                url: `/Auth/GetToken?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`,
                method: 'GET',
            }),
            transformResponse: (response) => {
                if (!response?.isSuccess) {
                    throw new Error(response?.errors?.[0] || 'Failed to retrieve token');
                }
                return response.value;
            },
        }),
        
        refreshAuthToken: builder.mutation({
            query: (refreshToken) => ({
                url: '/api/auth/refresh',
                method: 'POST',
                body: { refreshToken }
            }),
            transformResponse: (response) => {
                if (!response?.isSuccess || !response?.value?.token) {
                    throw new Error(response?.errors?.[0] || 'Token refresh failed');
                }
                return response.value;
            },
        }),
        
        // **TENANT ENDPOINTS**
        // Get all tenants with pagination
        getTenants: builder.query({
            query: (params = {}) => {
                // Clean and prepare query parameters
                const queryParams = {
                    page: typeof params === 'number' ? params : (params.page || 0),
                    pageSize: params.pageSize || 8,
                    ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
                    ...(params.status !== undefined && { status: params.status }),
                    ...(params.industry !== undefined && { industry: params.industry }),
                    ...(params.companySize !== undefined && { companySize: params.companySize }),
                    // Only include sort parameters when user explicitly sorts
                    // Allow sorting by sortable fields (not arrays like contactPersons, emails, phones)
                    ...(params.sortBy && ['name', 'location', 'createdDate', 'industry', 'companySize', 'activeStatus'].includes(params.sortBy) && { sortBy: params.sortBy }),
                    ...(params.sortOrder && ['name', 'location', 'createdDate', 'industry', 'companySize', 'activeStatus'].includes(params.sortBy) && { sortOrder: params.sortOrder })
                };

                console.log('🔍 Tenant API Query Params (keyword):', queryParams);

                return {
                    url: '/tenant/list',
                    method: 'GET',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                if (!response) {
                    return {
                        items: [],
                        totalCount: 0,
                        isSuccess: false
                    };
                }

                return {
                    items: response.value.tenants || [],
                    totalCount: response.value.totalCount,
                    isSuccess: response.isSuccess
                };
            },
            providesTags: ['Tenant']
        }),
        // Get single tenant
        getTenantById: builder.query({
            query: (tenantId) => ({
                url: `/tenant/${tenantId}`,
                method: 'GET'
            }),
            transformResponse: (response) => {
                // Get the base response
                const tenant = response?.value;
                
                if (!tenant) return null;
                
                // Convert single contactPerson/email to arrays if needed
                if (tenant.contactPerson && !tenant.contactPersons) {
                    tenant.contactPersons = [tenant.contactPerson];
                }
                if (tenant.email && !tenant.emails) {
                    tenant.emails = [tenant.email];
                }
                
                // Ensure contactPersons, emails, and phones are arrays
                tenant.contactPersons = Array.isArray(tenant.contactPersons) ? tenant.contactPersons : 
                                       (tenant.contactPerson ? [tenant.contactPerson] : []);
                tenant.emails = Array.isArray(tenant.emails) ? tenant.emails : 
                               (tenant.email ? [tenant.email] : []);
                tenant.phones = Array.isArray(tenant.phones) ? tenant.phones : 
                               (tenant.phone ? [tenant.phone] : []);
                
                // Process subTenants companies if they exist
                if (tenant.subTenants && Array.isArray(tenant.subTenants)) {
                    // Ensure each subTenants has the required fields - include ALL fields
                    tenant.subTenants = tenant.subTenants.map(subTenants => ({
                        id: subTenants.id || '',
                        name: subTenants.name || '',
                        emails: Array.isArray(subTenants.emails) ? subTenants.emails : 
                               (subTenants.email ? [subTenants.email] : []),
                        contactPersons: Array.isArray(subTenants.contactPersons) ? subTenants.contactPersons : 
                                       (subTenants.contactPerson ? [subTenants.contactPerson] : []),
                        phone: subTenants.phone || '', // Keep singular for backward compatibility
                        phones: Array.isArray(subTenants.phones) ? subTenants.phones : 
                               (subTenants.phone ? [subTenants.phone] : []), // Add phones array support
                        domain: subTenants.domain || '',
                        activeStatus: subTenants.activeStatus || false,
                        location: subTenants.location || '',
                        logo: subTenants.logo || '',
                        website: subTenants.website || '',
                        description: subTenants.description || '',
                        industry: subTenants.industry || 0,
                        companySize: subTenants.companySize || 0,
                        visualIdentity: subTenants.visualIdentity || null,
                        socialMedia: subTenants.socialMedia || null
                    }));
                } else {
                    tenant.subTenants = [];
                }
                
                return tenant;
            },
            providesTags: (result, error, id) => [
                { type: 'Tenant', id }, 
                'ModuleAccess',
                'Subsidiaries',
                'Videos'
            ]
        }),

        // Create new tenant
        createTenant: builder.mutation({
            query: (tenant) => {
                // Transform single values to arrays if needed
                const transformedTenant = {
                    ...tenant,
                    contactPersons: Array.isArray(tenant.contactPersons) ? tenant.contactPersons : 
                                   (tenant.contactPerson ? [tenant.contactPerson] : tenant.contactPersons || []),
                    emails: Array.isArray(tenant.emails) ? tenant.emails : 
                           (tenant.email ? [tenant.email] : tenant.emails || []),
                    phones: Array.isArray(tenant.phones) ? tenant.phones : 
                           (tenant.phone ? [tenant.phone] : tenant.phones || [])
                };
                
                // Remove old single fields
                delete transformedTenant.contactPerson;
                delete transformedTenant.email;
                
                return {
                    url: '/tenant/admin/create',
                    method: 'POST',
                    body: transformedTenant
                };
            },
            transformResponse: (response) => response?.value,
            invalidatesTags: ['Tenant', 'ModuleAccess', 'Subsidiaries', 'Videos']
        }),

        // Create tenant as admin with subsidiaries
        createAdminTenant: builder.mutation({
            query: (data) => {
                // Transform subTenants to match the API format
                const transformedData = {
                    ...data,
                    // Ensure required fields are present
                    name: data.name || '',
                    emails: Array.isArray(data.emails) ? data.emails : 
                           (data.email ? [data.email] : []),
                    contactPersons: Array.isArray(data.contactPersons) ? data.contactPersons : 
                                   (data.contactPerson ? [data.contactPerson] : []),
                    phones: Array.isArray(data.phones) ? data.phones : 
                           (data.phone ? [data.phone] : []),
                    location: data.location || '',
                    // Optional fields with defaults
                    website: data.website || '',
                    description: data.description || '',
                    logo: data.logo || '',
                    activeStatus: true,
                    industry: data.industry || 0,
                    companySize: data.companySize || 0,
                    domain: data.domain || '',
                    visualIdentity: {
                        primaryColor: data.visualIdentity?.primaryColor || '#3A9355',
                        secondaryColor: data.visualIdentity?.secondaryColor || '#6366F1'
                    },
                    socialMedia: {
                        linkedIn: data.socialMedia?.linkedIn || '',
                        twitter: data.socialMedia?.twitter || '',
                        facebook: data.socialMedia?.facebook || '',
                        instagram: data.socialMedia?.instagram || ''
                    },
                    // Transform subTenants to match the expected format
                    subTenants: (data.subTenants || []).map(sub => ({
                        name: sub.name || '',
                        contactPersons: Array.isArray(sub.contactPersons) ? sub.contactPersons : 
                                       (sub.contactPerson ? [sub.contactPerson] : []),
                        emails: Array.isArray(sub.emails) ? sub.emails : 
                               (sub.email ? [sub.email] : []),
                        phones: Array.isArray(sub.phones) ? sub.phones : 
                               (sub.phone ? [sub.phone] : []),
                        website: sub.website || '',
                        location: sub.location || '',
                        description: sub.description || '',
                        logo: sub.logo || '',
                        domain: sub.domain || '',
                        activeStatus: sub.activeStatus !== undefined ? sub.activeStatus : true,
                        visualIdentity: {
                            primaryColor: sub.visualIdentity?.primaryColor || '#3A9355',
                            secondaryColor: sub.visualIdentity?.secondaryColor || '#6366F1'
                        },
                        socialMedia: {
                            linkedIn: sub.socialMedia?.linkedIn || '',
                            twitter: sub.socialMedia?.twitter || '',
                            facebook: sub.socialMedia?.facebook || '',
                            instagram: sub.socialMedia?.instagram || ''
                        }
                    }))
                };

                // Remove old single fields
                delete transformedData.contactPerson;
                delete transformedData.email;
                delete transformedData.phone;
                
                // Remove phone from subTenants
                if (transformedData.subTenants) {
                    transformedData.subTenants.forEach(sub => {
                        delete sub.contactPerson;
                        delete sub.email;
                        delete sub.phone;
                    });
                }

                return {
                    url: '/tenant/admin/create',
                    method: 'POST',
                    body: transformedData
                };
            },
            transformResponse: (response) => {
                if (!response?.isSuccess) {
                    throw new Error(response?.errors?.join(', ') || 'Failed to create tenant and subsidiaries');
                }
                return response.value;
            },
            transformErrorResponse: (response) => {
                return {
                    status: response.status,
                    message: response.data?.message || 'Failed to create tenant and subsidiaries'
                };
            },
            invalidatesTags: ['Tenant', 'ModuleAccess', 'Subsidiaries', 'Videos']
        }),

        // Update tenant
        updateTenant: builder.mutation({
            query: (tenant) => {
                // Make sure tenantId is properly included
                if (!tenant.tenantId) {
                    throw new Error('TenantId is required for update operation');
                }
                
                // Transform single values to arrays if needed
                const transformedTenant = {
                    ...tenant,
                    contactPersons: Array.isArray(tenant.contactPersons) ? tenant.contactPersons : 
                                   (tenant.contactPerson ? [tenant.contactPerson] : tenant.contactPersons || []),
                    emails: Array.isArray(tenant.emails) ? tenant.emails : 
                           (tenant.email ? [tenant.email] : tenant.emails || []),
                    phones: Array.isArray(tenant.phones) ? tenant.phones : 
                           (tenant.phone ? [tenant.phone] : tenant.phones || [])
                };
                
                // Remove old single fields
                delete transformedTenant.contactPerson;
                delete transformedTenant.email;
                delete transformedTenant.phone;
                
                // Remove phone from subTenants if exists
                if (transformedTenant.subTenants) {
                    transformedTenant.subTenants.forEach(sub => {
                        delete sub.contactPerson;
                        delete sub.email;
                        delete sub.phone;
                    });
                }
                
                return {
                    url: '/tenant/update',
                    method: 'PUT',
                    body: transformedTenant
                };
            },
            transformResponse: (response) => {
                // Handle response status and provide more information on error
                if (response?.resultStatus !== ResultStatus.OK && 
                    response?.resultStatus !== ResultStatus.CREATED) {
                    const error = new Error(response?.errors?.join(', ') || 'Unknown error updating tenant');
                    error.data = response;
                    throw error;
                }
                return response?.value;
            },
            invalidatesTags: (result, error, { tenantId }) => [
                { type: 'Tenant', id: tenantId },
                'Tenant', 
                'ModuleAccess',
                'Subsidiaries',
                'Videos'
            ]
        }),
        uploadLogo: builder.mutation({
            query: ({ orderNo, file }) => {
                const formData = new FormData();
                formData.append('orderNo', orderNo);
                formData.append('file', file);

                return {
                    url: '/tenant/logo',
                    method: 'POST',
                    body: formData,
                    formData: true,
                };
            },
            transformResponse: (response) => response?.value,
            invalidatesTags: ['Tenant']
        }),

        
        // Get tenant subsidiaries
        getTenantSubsidiaries: builder.query({
            query: (tenantId) => ({
                url: `/tenant/${tenantId}/subsidiaries`,
                method: 'GET',
            }),
            transformResponse: (response) => {
                if (!response || !response.isSuccess) {
                    return [];
                }
                
                return response.value || [];
            },
            providesTags: (result, error, tenantId) => [
                { type: 'Subsidiaries', id: tenantId }
            ]
        }),
        
        // Get tenant videos status
        getTenantVideos: builder.query({
            query: (tenantId) => ({
                url: `/tenant/${tenantId}/videos`,
                method: 'GET',
            }),
            transformResponse: (response) => {
                if (!response || !response.isSuccess) {
                    return {
                        isCompleted: false,
                        steps: {
                            companyProfile: false,
                            branding: false,
                            userSetup: false,
                            moduleConfig: false
                        },
                        progress: 0
                    };
                }
                
                return response.value;
            },
            providesTags: (result, error, tenantId) => [
                { type: 'Videos', id: tenantId }
            ]
        }),

        getAllTenants: builder.query({
            query: () => ({
                url: '/tenant/get-all',
                method: 'GET',
            }),
            transformResponse: (response) => {
                if (!response) {
                    return {
                        items: [],
                        isSuccess: false,
                        successMessage: '',
                        errors: [],
                        validationErrors: [],
                    };
                }
                return {
                    items: response.value || [],
                    isSuccess: response.isSuccess,
                    successMessage: response.successMessage,
                    errors: response.errors,
                    validationErrors: response.validationErrors,
                };
            },
            providesTags: ['Tenant'],
        }),
        // Add this to the endpoints object in tenantApi
        getCities: builder.query({
            query: () => ({
                url: '/city/list',
                method: 'GET',
            }),
            transformResponse: (response) => {
                if (!response?.isSuccess || !response?.value) {
                    return [];
                }
                // Ensure we return an array even if the response structure is different
                const cities = Array.isArray(response.value) ? response.value : 
                             Array.isArray(response.value.items) ? response.value.items : [];
                return cities;
            }
        }),
        getTenantInfo: builder.query({
            query: () => ({
                url: '/tenant/mytenant',
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (!response?.isSuccess) {
                    return null;
                }
                return response;
            },
            providesTags: ['TenantModules', 'TenantPermissions'],
            // Enhanced caching and refetching for critical tenant data
            pollingInterval: 5 * 60 * 1000, // Poll every 5 minutes for updates
            refetchOnFocus: true,
            refetchOnReconnect: true
        }),
        // Delete tenant
        deleteTenant: builder.mutation({
            query: (tenantId) => ({
                url: `/tenant/delete/${tenantId}`,
                method: 'DELETE'
            }),
            onQueryStarted: async (tenantId, { queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    // Delete all mock data for this tenant
                    if (mockSubsidiariesStorage[tenantId]) {
                        delete mockSubsidiariesStorage[tenantId];
                    }
                    if (mockVideosStorage[tenantId]) {
                        delete mockVideosStorage[tenantId];
                    }
                } catch (error) {
                    console.error('Failed to delete tenant:', error);
                }
            },
            transformResponse: (response) => response?.value,
            invalidatesTags: ['Tenant', 'ModuleAccess', 'Subsidiaries', 'Videos']
        }),
        // Add this new endpoint to get current tenant ID
        getTenantId: builder.query({
            query: () => '/tenant/getcurrenttenantid',
            transformResponse: (response) => response?.value || null,
            providesTags: ['Tenant']
        }),
        // Get all available tenants for parent company relationship
        getAvailableTenants: builder.query({
            query: (excludeId) => ({
                url: '/tenant/available',
                method: 'GET',
                params: { excludeId }
            }),
            // Mock endpoint that returns a list of companies excluding the current one
            queryFn: (excludeId) => {
                // Create a list of mock companies
                const mockCompanies = [
                    { id: '1', name: 'Acme Corporation', location: 'New York' },
                    { id: '2', name: 'Globex Industries', location: 'Chicago' },
                    { id: '3', name: 'Soylent Corp', location: 'San Francisco' },
                    { id: '4', name: 'Initech', location: 'Austin' },
                    { id: '5', name: 'Umbrella Corporation', location: 'London' },
                    { id: '6', name: 'Stark Industries', location: 'Malibu' },
                    { id: '7', name: 'Wayne Enterprises', location: 'Gotham City' }
                ];
                
                // Filter out the excludeId if provided
                const filteredCompanies = excludeId 
                    ? mockCompanies.filter(company => company.id !== excludeId)
                    : mockCompanies;
                
                return {
                    data: {
                        value: filteredCompanies,
                        isSuccess: true
                    }
                };
            },
            providesTags: ['Tenant']
        }),
        // Kullanıcı izinlerini getiren yeni endpoint
        getUserPermissions: builder.query({
            query: () => ({
                url: '/tenant/izinler',
                method: 'GET',
            }),
            // 404 veya boş yanıtlar için bile tutarlı veri döndürme
            transformResponse: (response, meta, arg) => {
                // API yanıtı başarılı değilse veya yanıt yoksa
                if (!response || !response.isSuccess) {
                    console.warn('No permissions data in response or response failed:', response);
                    return {
                        permissions: [],
                        isSuccess: false,
                        message: response?.message || 'Permissions could not be retrieved'
                    };
                }
                
                // API yanıtı başarılı ama içinde veri yoksa
                if (!response.value || !Array.isArray(response.value)) {
                    console.warn('No permissions array in successful response:', response);
                    return {
                        permissions: [],
                        isSuccess: true,
                        message: 'No permissions defined for this user'
                    };
                }
                
                // Başarılı ve verili yanıt
                return {
                    permissions: response.value,
                    isSuccess: true,
                    message: 'Permissions retrieved successfully'
                };
            },
            // Hata durumları için tutarlı veri dönüşü
            transformErrorResponse: (response, meta, arg) => {
                // 404 No Content için bile izinleri boş dizi olarak dön
                if (response.status === 404) {
                    console.warn('Permissions endpoint not found (404)');
                    return {
                        permissions: [],
                        isSuccess: false,
                        message: 'Permissions endpoint not available',
                        errorStatus: 404
                    };
                }
                
                // Diğer hatalar için
                console.error('Error fetching permissions:', response);
                return {
                    permissions: [],
                    isSuccess: false,
                    message: 'Failed to retrieve permissions',
                    errorStatus: response.status
                };
            },
            // Her login sonrası yeniden çek
            providesTags: ['UserPermissions'],
        }),
        // Add new endpoint for creating single subTenants
        createTenantAdmin: builder.mutation({
            query: (subTenants) => {
                // Transform single values to arrays if needed
                const transformedsubTenants = {
                    ...subTenants,
                    contactPersons: Array.isArray(subTenants.contactPersons) ? subTenants.contactPersons : 
                                   (subTenants.contactPerson ? [subTenants.contactPerson] : subTenants.contactPersons || []),
                    emails: Array.isArray(subTenants.emails) ? subTenants.emails : 
                           (subTenants.email ? [subTenants.email] : subTenants.emails || [])
                };
                
                // Remove old single fields
                delete transformedsubTenants.contactPerson;
                delete transformedsubTenants.email;
                
                return {
                    url: '/tenant/tenant-admin/create',
                    method: 'POST',
                    body: transformedsubTenants
                };
            },
            // Transform response to handle success/error cases
            transformResponse: (response) => {
                if (!response?.isSuccess) {
                    throw new Error(response?.errors?.join(', ') || 'Failed to create subTenants');
                }
                return response.value;
            },
            // Transform error response for consistent error handling
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to create subTenants'
            }),
            // Invalidate relevant cache tags after successful creation
            invalidatesTags: ['Tenant', 'Subsidiaries']
        }),
        // Add new endpoint for uploading subTenants logo
        uploadsubTenantsLogo: builder.mutation({
            query: (file) => {
                const formData = new FormData();
                formData.append('file', file);

                return {
                    url: '/tenant/logo',
                    method: 'POST',
                    body: formData,
                    formData: true,
                };
            },
            transformResponse: (response) => {
                if (!response?.isSuccess) {
                    throw new Error(response?.errors?.join(', ') || 'Failed to upload logo');
                }
                return response.value;
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to upload logo'
            })
        }),
        // Add new endpoint for updating subTenants as tenant admin
        updateTenantAdmin: builder.mutation({
            query: (subTenants) => {
                // Transform single values to arrays if needed
                const transformedsubTenants = {
                    ...subTenants,
                    contactPersons: Array.isArray(subTenants.contactPersons) ? subTenants.contactPersons : 
                                   (subTenants.contactPerson ? [subTenants.contactPerson] : subTenants.contactPersons || []),
                    emails: Array.isArray(subTenants.emails) ? subTenants.emails : 
                           (subTenants.email ? [subTenants.email] : subTenants.emails || [])
                };
                
                // Remove old single fields
                delete transformedsubTenants.contactPerson;
                delete transformedsubTenants.email;
                
                return {
                    url: '/tenant/tenant-admin/update',
                    method: 'PUT',
                    body: transformedsubTenants
                };
            },
            transformResponse: (response) => {
                if (!response?.isSuccess) {
                    throw new Error(response?.errors?.join(', ') || 'Failed to update subTenants');
                }
                return response.value;
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to update subTenants'
            }),
            invalidatesTags: ['Tenant', 'Subsidiaries']
        }),

        updateMyTenant: builder.mutation({
            query: (body) => {
                const {
                    id,
                    activeStatus,
                    tenantType,
                    parentTenantId,
                    contactPerson,
                    email,
                    phone,
                    ...rest
                } = body;

                const transformed = {
                    ...rest,
                    contactPersons: Array.isArray(rest.contactPersons)
                        ? rest.contactPersons
                        : (contactPerson ? [contactPerson] : rest.contactPersons || []),
                    emails: Array.isArray(rest.emails)
                        ? rest.emails
                        : (email ? [email] : rest.emails || []),
                    phones: Array.isArray(rest.phones)
                        ? rest.phones
                        : (phone ? [phone] : rest.phones || []),
                };

                return {
                    url: '/tenant/my-tenant',
                    method: 'PUT',
                    body: transformed,
                };
            },
            transformResponse: (response) => {
                if (!response?.isSuccess) {
                    throw new Error(response?.errors?.join(', ') || 'Failed to update company');
                }
                return response.value;
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to update company',
            }),
            invalidatesTags: ['Tenant', 'TenantModules', 'TenantPermissions'],
        }),

        // Add new endpoint for updating tenant with subsidiaries as admin
        updateTenantAdminWithSubsidiaries: builder.mutation({
            query: (data) => {
                // Transform main tenant data
                const transformedData = {
                    ...data,
                    contactPersons: Array.isArray(data.contactPersons) ? data.contactPersons : 
                                   (data.contactPerson ? [data.contactPerson] : data.contactPersons || []),
                    emails: Array.isArray(data.emails) ? data.emails : 
                           (data.email ? [data.email] : data.emails || []),
                    // Transform subTenants
                    subTenants: (data.subTenants || []).map(sub => ({
                        ...sub,
                        contactPersons: Array.isArray(sub.contactPersons) ? sub.contactPersons : 
                                       (sub.contactPerson ? [sub.contactPerson] : []),
                        emails: Array.isArray(sub.emails) ? sub.emails : 
                               (sub.email ? [sub.email] : [])
                    }))
                };

                // Remove old single fields
                delete transformedData.contactPerson;
                delete transformedData.email;
                
                return {
                    url: '/tenant/admin/update',
                    method: 'PUT',
                    body: transformedData
                };
            },
            transformResponse: (response) => {
                if (!response?.isSuccess) {
                    throw new Error(response?.errors?.join(', ') || 'Failed to update tenant and subsidiaries');
                }
                return response.value;
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to update tenant and subsidiaries'
            }),
            invalidatesTags: ['Tenant', 'Subsidiaries']
        }),
        // Get categories list
        getCategoryList: builder.query({
            query: () => ({
                url: '/category/list',
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (!response?.isSuccess || !response?.value) {
                    return [];
                }
                // Ensure we return an array even if the response structure is different
                const categories = Array.isArray(response.value) ? response.value : 
                                 Array.isArray(response.value.items) ? response.value.items : [];
                return categories;
            }
        }),
        // Get tenant subsidiaries with search support
        getTenantSubsidiariesWithSearch: builder.query({
            query: ({ tenantId, keyword = '', page = 0, pageSize = 9, status = '' }) => {
                let url = `/tenant/${tenantId}/subsidiaries?page=${page}&pageSize=${pageSize}`;
                
                if (keyword.trim()) {
                    url += `&keyword=${encodeURIComponent(keyword.trim())}`;
                }
                if (status.trim()) {
                    url += `&status=${encodeURIComponent(status)}`;
                }
                
                return {
                    url,
                    method: 'GET',
                };
            },
            transformResponse: (response) => {
                if (!response || !response.isSuccess) {
                    return {
                        subTenants: [],
                        totalCount: 0,
                        isSuccess: false
                    };
                }
                
                const subsidiaries = response.value?.subTenants || response.value || [];
                
                // Ensure each subTenants has the required fields
                const processedSubsidiaries = Array.isArray(subsidiaries) ? subsidiaries.map(subTenants => ({
                    id: subTenants.id || '',
                    name: subTenants.name || '',
                    emails: Array.isArray(subTenants.emails) ? subTenants.emails : 
                           (subTenants.email ? [subTenants.email] : []),
                    contactPersons: Array.isArray(subTenants.contactPersons) ? subTenants.contactPersons : 
                                   (subTenants.contactPerson ? [subTenants.contactPerson] : []),
                    phone: subTenants.phone || '',
                    domain: subTenants.domain || '',
                    activeStatus: subTenants.activeStatus || false,
                    location: subTenants.location || '',
                    logo: subTenants.logo || '',
                    website: subTenants.website || '',
                    description: subTenants.description || '',
                    industry: subTenants.industry || 0,
                    companySize: subTenants.companySize || 0,
                    visualIdentity: subTenants.visualIdentity || null,
                    socialMedia: subTenants.socialMedia || null
                })) : [];
                
                return {
                    subTenants: processedSubsidiaries,
                    totalCount: response.value?.totalCount || processedSubsidiaries.length,
                    isSuccess: true
                };
            },
            providesTags: (result, error, { tenantId }) => [
                { type: 'Subsidiaries', id: tenantId }
            ]
        }),

        // Admin endpoints for module management
        disableTenantModules: builder.mutation({
            query: ({ tenantId, disabledModules }) => ({
                url: `/admin/tenant/${tenantId}/disable-modules`,
                method: 'POST',
                body: { disabledModules }
            }),
            transformResponse: (response, meta) => {
                console.log('Disable modules response:', response);
                console.log('Disable modules meta:', meta);
                
                // Handle 200 OK with empty response
                if (meta?.response?.status === 200 && (!response || response === '')) {
                    return { isSuccess: true, message: 'Modules updated successfully' };
                }
                
                // Handle standard response format
                if (response && typeof response === 'object') {
                    if (response.isSuccess === false) {
                        throw new Error(response?.errors?.[0] || 'Failed to disable modules');
                    }
                    return response;
                }
                
                // Default success for 200 status
                if (meta?.response?.status === 200) {
                    return { isSuccess: true, message: 'Modules updated successfully' };
                }
                
                throw new Error('Unexpected response format');
            },
            invalidatesTags: (result, error, { tenantId }) => [
                { type: 'Tenant', id: tenantId },
                'TenantModules',
                'TenantPermissions'
            ]
        }),

        enableTenantModules: builder.mutation({
            query: ({ tenantId, enabledModules }) => ({
                url: `/admin/tenant/${tenantId}/enable-modules`,
                method: 'POST',
                body: { enabledModules }
            }),
            transformResponse: (response, meta) => {
                console.log('Enable modules response:', response);
                console.log('Enable modules meta:', meta);
                
                // Handle 200 OK with empty response
                if (meta?.response?.status === 200 && (!response || response === '')) {
                    return { isSuccess: true, message: 'Modules updated successfully' };
                }
                
                // Handle standard response format
                if (response && typeof response === 'object') {
                    if (response.isSuccess === false) {
                        throw new Error(response?.errors?.[0] || 'Failed to enable modules');
                    }
                    return response;
                }
                
                // Default success for 200 status
                if (meta?.response?.status === 200) {
                    return { isSuccess: true, message: 'Modules updated successfully' };
                }
                
                throw new Error('Unexpected response format');
            },
            invalidatesTags: (result, error, { tenantId }) => [
                { type: 'Tenant', id: tenantId },
                'TenantModules',
                'TenantPermissions'
            ]
        }),

        // Tenant admin endpoint for user permission management
        setUserPermissions: builder.mutation({
            query: ({ userId, permissions }) => ({
                url: `/tenant-admin/user/${userId}/permissions`,
                method: 'POST',
                body: { permissions }
            }),
            transformResponse: (response) => {
                if (!response || !response.isSuccess) {
                    throw new Error(response?.errors?.[0] || 'Failed to set user permissions');
                }
                return response;
            },
            invalidatesTags: (result, error, { userId }) => [
                { type: 'UserPermissions', id: userId },
                'Tenant'
            ]
        }),
    })
});

export const {
    // Auth hooks
    useLoginGetCodeMutation,
    useVerifyAndGetTokenMutation,
    useRefreshAuthTokenMutation,
    // Tenant hooks
    useGetTenantsQuery,
    useGetTenantByIdQuery,
    useCreateTenantMutation,
    useCreateAdminTenantMutation,
    useUpdateTenantMutation,
    useDeleteTenantMutation,
    useUploadLogoMutation,
    useGetAllTenantsQuery,
    useGetTenantInfoQuery,
    useGetCitiesQuery,
    useGetCategoryListQuery,
    useGetTenantIdQuery,
    useGetUserPermissionsQuery,
    useGetTenantSubsidiariesQuery,
    useGetTenantSubsidiariesWithSearchQuery,
    useGetTenantVideosQuery,
    useGetAvailableTenantsQuery,
    useCreateTenantAdminMutation,
    useUploadsubTenantsLogoMutation,
    useUpdateTenantAdminMutation,
    useUpdateMyTenantMutation,
    useUpdateTenantAdminWithSubsidiariesMutation,
    useDisableTenantModulesMutation,
    useEnableTenantModulesMutation,
    useSetUserPermissionsMutation,
    util: { resetApiState }
} = tenantApi;